/**
 * records.js
 */

import React from 'react';
import axios from 'axios';
import {
  useMediaQuery,
  Badge,
  Box,
  Card,
  Container,
  Chip,
} from '@mui/material';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { parse, format } from 'date-fns';
import { TOUR_BEACON_VIEW_LIMIT } from 'utils/constants';
import { dateParamFormat } from 'utils/datetime';
import { getFilterParamsObj } from 'utils/filter-params';
import logger from 'utils/logger';
import useAsyncInterval from 'utils/use-async-interval';
import { renderForMobileContext } from 'utils/app-context';
import { UserContext } from 'contexts/user/user-context';
import { DogsContext } from 'contexts/dogs/dogs-context';
import { TourContext } from 'contexts/tour/tour-context';
import { AppMessagingContext } from 'contexts/app-messaging/app-messaging-context';
import { TRACKING_STATE } from 'components/tracking/tracking-common';
import { LiveTrackingContext } from 'components/tracking/live-tracking/live-tracking-context';
import useRecordStore from 'contexts/record-store';
import { AppBarContext } from 'contexts/app-bar/app-bar-context';
import { ViewingMapContext } from 'components/tracking/map/viewing-map-context';
import RouteLink from 'routing/route-link';
import Icon from 'components/icon';
import FeatureBox from 'components/feature-box';
import useTours from 'components/tour/tour-data';
import DropMenu from 'components/drop-menu';
import TrainingDrawer from 'components/training/training-drawer';
import DeploymentDrawer from 'components/deployment/drawer';
import TrainingClassDrawer from 'components/training-class/drawer';
import VetVisitDrawer from 'components/vet-visit/drawer';
import BarButton from 'components/layout/app-bar/bar-button';
import { ViewingMapProvider } from 'components/tracking/map/viewing-map-context';
import { TRACK_TYPE } from 'components/tracking/tracking-common';
import { RecordsProvider, RecordsContext } from './records-context';
import DesktopHeader from './desktop-header';
import RecordCalendar from './record-calendar';
import RecordList from './record-list';
import QuickLinks from './quick-links';
import FilterDialog from './filter-dialog';


const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
const REFRESH_HAS_LIVE_TRACKS_MS = 1 * 60 * 1000; // 1 minutes


const Records = (props) => {
  let history = useHistory();
  const path = history.location.pathname;
  const basePath = useRouteMatch();
  const params = basePath.params;
  const filterMode = params.filterMode || '';
  const filterOption = params.filterOption || '';
  const filterParamsString = params.filterParamsString;
  const filterParams = getFilterParamsObj(filterParamsString);
  const calendarDate = filterParams.date || null;
  const pageNum = parseInt(params.pageNum) || 1;

  const recordStore = useRecordStore();
  const status = recordStore.useStatus();
  const isInitializing = status.isInitializing.get();
  const hasLiveDeploymentTracks = status.stats.hasLiveDeploymentTracks.get();
  const hasQuickLinks = (status.stats.incompleteCount.get() 
    + status.stats.notReviewedCount.get()
    + status.stats.lateCompletionCount.get()
    + status.stats.rejectedCount.get()
    + status.stats.outdatedCount.get()
    + status.stats.notReviewedCount.get()
  ) > 0;

  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { startTour } = useTours();
  const { user } = React.useContext(UserContext);
  const { dogs } = React.useContext(DogsContext);
  const { activeTour, toursSeen, beaconViews } = React.useContext(TourContext);
  const { appInfo } = React.useContext(AppMessagingContext);
  const { setAppBar } = React.useContext(AppBarContext);
  const { trackingState, setIsTrackingOpen } = React.useContext(LiveTrackingContext);
  const { 
    searchOptions,
    setSearchOptions,
    isReportDialogOpen, 
    setIsReportDialogOpen,
    isFilterOpen, 
    setIsFilterOpen 
  } = React.useContext(RecordsContext);  
  const { setMapParams, setIsDialogOpen } = React.useContext(ViewingMapContext);
  const showLiveMapLink = user.role.isSupervisor && hasLiveDeploymentTracks;
  

  logger.log(
    '[records.js] Rendering /records page. filter tab: %s, filter option: %s, params: %s, pageNum: %s', 
    filterMode,
    filterOption,
    filterParamsString, 
    pageNum);


  // refresh records
  useAsyncInterval(async () => {
    if (!isReportDialogOpen) {
      await recordStore.refreshRecords();
    }
  }, REFRESH_INTERVAL_MS);


  // refresh live track status
  useAsyncInterval(async () => {
    if (!isReportDialogOpen) {
      await recordStore.updateLiveTrackStatus();
    }
  }, REFRESH_HAS_LIVE_TRACKS_MS);


  React.useEffect(() => {
    document.title = 'PACKTRACK - Records';
  }, []);


  // re-fetch options when they could have changed
  React.useEffect(() => {
    (async () => {      
      const data = await recordStore.setFilter(getFilterParamsObj(filterParamsString), pageNum);

      if (data?.options) {
        setSearchOptions(data.options);
      } 
      // if user navigates away from records we have to re-fetch search options
      // when they return. i considered caching in store but options should refresh 
      // if user has done other actions that potentially affect them.
      else if (!searchOptions) {
        const response = await axios.get('/records/options');
        if (response.status === 200) {
          setSearchOptions(response.data);
        }
      }

      // scroll down so results aren't below the fold
      const listHeaderElement = document.querySelector('#records-list-header');
      if (listHeaderElement) {
        setTimeout(() => listHeaderElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 10);
      }
    })();
  // recordStore can't be in
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMode, filterParamsString, pageNum, searchOptions]);


  // check if we should start a product tour
  React.useEffect(() => {
    const isTourConditionsValid = activeTour < 0 && !isMobile && user !== null && dogs.length > 0;
    const isTourPathValid = path.includes("/records") && !path.includes("drawer");
    if (!isInitializing && isTourConditionsValid && isTourPathValid) {
      const pageBeaconCount = beaconViews[0] ?? 0;
      const listBeaconCount = beaconViews[6] ?? 0;

      if (!toursSeen.includes(0) && user.completeExercisesCount >= 0 && pageBeaconCount <= TOUR_BEACON_VIEW_LIMIT) {
        setTimeout(() => startTour(0), 1000); // Records page tour
      }
      else if (toursSeen.includes(1) && toursSeen.includes(2) &&
        !toursSeen.includes(6) && (user.completeExercisesCount) >= 5 && listBeaconCount <= TOUR_BEACON_VIEW_LIMIT) {
        setTimeout(() => startTour(6), 1000); // Records list tour
      }
    }
  }, [path, isInitializing, isMobile, user, dogs, activeTour, toursSeen, beaconViews, startTour]);


  const addRecordMenuItems = React.useMemo(() => {
    const menuItems = [];

    // TRAINING
    if (user.role.isHandler || user.role.isTrainer) {
      menuItems.push({
        name: 'Training Record',
        icon: 'event',
        key: 'add-training',
        component: RouteLink,
        basePath: basePath.url,
        pathSectionName: 'training',
        pathItemName: 'wizardStart',
        pathParams: { eventId: 0 },
        queryParams: calendarDate || !filterParamsString
          ? { date: calendarDate || format(new Date(), dateParamFormat) }
          : null,
        disabled: !user.role.status.isActive
      });
    }

    if (user.role.isHandler) {
      // DEPLOYMENT
      menuItems.push({
        name: 'Deployment Record',
        icon: 'deployment',
        key: 'add-deployment',
        component: RouteLink,
        basePath: basePath.url,
        pathSectionName: 'deployment',
        pathItemName: 'edit',
        pathParams: {
          deploymentId: 0,
          viewSectionId: 0,
          editSectionId: 0,
        },
        queryParams: calendarDate ? { date: calendarDate } : null,
        disabled: !user.role.status.isActive
          || (calendarDate && parse(calendarDate, dateParamFormat, new Date()) > new Date()),
      });
      // CLASS
      menuItems.push({
        name: 'Class Record',
        icon: 'class',
        key: 'add-class',
        component: RouteLink,
        basePath: basePath.url,
        pathSectionName: 'class',
        pathItemName: 'edit',
        pathParams: { classId: 0 },
        queryParams: calendarDate ? { date: calendarDate } : null,
        disabled: !user.role.status.isActive,
      });
      menuItems.push({
        name: 'Vet Visit',
        icon: 'vet-visit',
        key: 'add-vet-visit',
        component: RouteLink,
        basePath: basePath.url,
        pathSectionName: 'vetVisit',
        pathItemName: 'edit',
        pathParams: { vetVisitId: 0 },
        queryParams: calendarDate ? { date: calendarDate } : null,
        disabled: !user.role.status.isActive,
      });
    }
    
    // TRACKING
    if (renderForMobileContext()) {
      const locationLabel = trackingState !== TRACKING_STATE.UNSTARTED
        ? 'Return to Track'
        : user.role.isHandler
          ? 'Location Track'
          : 'Lay Track';
      if (menuItems.length > 0) {
        menuItems.push({ id: 'divider' });
      }
      menuItems.push({
        name: locationLabel,
        icon: 'patrol-tracking',
        key: 'add-track',
        disabled: appInfo?.version <= '4.6.1',
        onSelect: () => setIsTrackingOpen(true),
      });
    }

    return menuItems;
  }, [
    user.role.isHandler,
    user.role.isTrainer,
    user.role.status.isActive,
    filterParamsString, 
    calendarDate, 
    basePath.url,
    trackingState,
    setIsTrackingOpen,
    appInfo,
  ]);


  // mobile app-bar
  React.useEffect(() => {
    if (isInitializing) {
      setAppBar('Records', []);
    } else {
      // build menu items
      const dropMenuItems = [];
      if (user.role.isHandler) {
        dropMenuItems.push({
          name: <>
            Solo Quick Training
            <Chip
              size='small'
              className='chip-ok chip-font-ok'
              sx={{ ml: 1 }}
              label='NEW!'
            />
          </>,
          icon: 'quick-training',
          key: 'quick-training',
          component: RouteLink,
          basePath: basePath.url,
          pathSectionName: 'training',
          pathItemName: 'quickTraining'
        });
        dropMenuItems.push({ id: 'divider' });
      }
      if (addRecordMenuItems.length > 0) {
        dropMenuItems.push(...addRecordMenuItems);
        dropMenuItems.push({ id: 'divider' });
      }
      dropMenuItems.push({
        name: 'Create Report',
        icon: 'report',
        key: 'create-report',
        onSelect: () => setIsReportDialogOpen(true),
      });      

      setAppBar('Records', [

        // QUICK LINKS
        hasQuickLinks
          ? <QuickLinks
            label={(quickLinkCount) =>
              <BarButton data-testid='quick-links-button'>
                <Badge
                  badgeContent={quickLinkCount}
                  color='secondary'
                  children={<Icon type='todo' />}
                />
              </BarButton>
            }
            activeRecordFilterId={filterOption}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}            
          />
          : '',

        // LIVE MAP
        showLiveMapLink
          ? <BarButton
            onClick={() => {
              setMapParams(TRACK_TYPE.MULTI_DEPLOYMENT);
              setIsDialogOpen(true);
            }}
            children={<Icon type='map' />}
          />
          : '',

        // FILTER
        <BarButton
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          children={<Icon type='custom' />}
        />,
  
        // MORE...
        <DropMenu
          id="add-menu"
          items={dropMenuItems}
          label={(isOpen, onClick, arrowIcon) => <BarButton
            color='secondary'
            data-testid='add-button'
            onClick={onClick}
            disabled={!user.role.status.isActive}
            wide
            children={<Icon type='add-box' style={{ fontSize: 32 }} />}
          />}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        />
      ]);
    }
  }, [
    user, 
    isInitializing, 
    isFilterOpen,
    hasQuickLinks, 
    filterOption,
    addRecordMenuItems,
    showLiveMapLink,
    basePath.url,
    setAppBar, 
    setIsFilterOpen,
    setIsReportDialogOpen,
    setIsDialogOpen,
    setMapParams
  ]);


  return (
    <Container 
      sx={theme => ({
        pt: 4,
        [theme.breakpoints.down('md')]: { pt: 3 },
        [theme.breakpoints.down('sm')]: { p: 1 }
      })} 
    >
      <DesktopHeader addRecordMenuItems={addRecordMenuItems} />

      <FeatureBox 
        loading={isInitializing} 
        message={isInitializing ? 'Initializing Application ...' : ''}
        style={{ minHeight: 140 }}
      >
        {/* Render hidden as early as possible to avoid visible calendar jump after first render */}
        <Box sx={{ 
          display: isInitializing ? 'none' : 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          '& > *': { flex: 1 }
        }}>
          <Card className='element-default-box-shadow'>
            <RecordCalendar maxCalendars={3} />
          </Card>
          <Box sx={{ mt: 4 }}>
            <RecordList />
          </Box>
        </Box>
      </FeatureBox>

      <TrainingDrawer />
      <DeploymentDrawer />
      <TrainingClassDrawer />
      <VetVisitDrawer />

      <FilterDialog />
    </Container>
  );
}


export default function RecordsWrap() {
  return (
    <RecordsProvider>
      <ViewingMapProvider>
        <Records />
      </ViewingMapProvider>
    </RecordsProvider>
  )
}